import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { getMangaById, getRecommendedManga } from '@/mocks/manga';
import { siteInfo } from '@/mocks/manga';

export default function MangaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const manga = id ? getMangaById(id) : undefined;
  const recommended = getRecommendedManga(4).filter((m) => m.id !== id).slice(0, 4);

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">漫画不存在</h1>
          <Link to="/" className="text-accent-600 hover:text-accent-700 mt-4 inline-block text-sm">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${manga.title} - ${manga.categoryLabel}腐漫 | ${siteInfo.name}`;
  const pageDesc = `${manga.title}，${manga.categoryLabel}腐漫作品。${manga.description.substring(0, 100)}免费全集下拉式阅读，${siteInfo.name}热门腐漫推荐。`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": manga.title,
      "description": manga.description,
      "image": manga.coverImage,
      "author": { "@type": "Person", "name": manga.author },
      "datePublished": manga.chapterList[manga.chapterList.length - 1].date,
      "dateModified": manga.updateTime,
      "publisher": {
        "@type": "Organization",
        "name": siteInfo.name,
        "url": "https://fumanhua.com"
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://fumanhua.com/manga/${manga.id}`
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://fumanhua.com" },
        { "@type": "ListItem", "position": 2, "name": manga.categoryLabel, "item": `https://fumanhua.com/category/${manga.category}` },
        { "@type": "ListItem", "position": 3, "name": manga.title }
      ]
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={manga.seoKeywords}
        canonical={`https://fumanhua.com/manga/${manga.id}`}
        ogType="article"
        ogImage={manga.coverImage}
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: manga.categoryLabel, to: `/category/${manga.category}` },
              { label: manga.title },
            ]} />

            <article className="flex flex-col md:flex-row gap-8 mb-14">
              <div className="flex-shrink-0 w-full md:w-[320px]">
                <div className="rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={manga.coverImage}
                    alt={`${manga.title} - ${manga.categoryLabel}腐漫封面`}
                    title={`${manga.title} - ${manga.seoKeywords}`}
                    className="w-full aspect-[3/4] object-cover"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {manga.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-500">作者：<strong>{manga.author}</strong></span>
                  <span className="px-2 py-0.5 text-xs rounded-full bg-accent-100 text-accent-700 font-medium">{manga.categoryLabel}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${manga.status === '已完结' ? 'bg-foreground-100 text-foreground-600' : 'bg-secondary-100 text-secondary-700'}`}>
                    {manga.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-4 mb-5 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <strong className="text-foreground-800">{manga.rating}</strong> 分
                  </span>
                  <span>
                    <strong className="text-foreground-800">{manga.views.toLocaleString()}</strong> 次阅读
                  </span>
                  <span>
                    共 <strong className="text-foreground-800">{manga.chapters}</strong> 话
                  </span>
                  <span>
                    更新于 <strong className="text-foreground-800">{manga.updateTime}</strong>
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {manga.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-4 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{manga.fullDescription}</p>
                </div>

                <Link
                  to={`/category/${manga.category}`}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500 text-background-50 font-medium rounded-full hover:bg-accent-600 transition-all duration-300 whitespace-nowrap text-sm"
                >
                  <i className="ri-book-open-line"></i>
                  开始阅读
                </Link>
              </div>
            </article>

            <section className="mb-14">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-heading font-bold text-foreground-950">章节目录</h2>
                <span className="text-sm text-foreground-400">共 {manga.chapters} 话</span>
              </div>
              <div className="bg-background-100 rounded-lg divide-y divide-background-200/70 max-h-[500px] overflow-y-auto">
                {manga.chapterList.map((ch) => (
                  <div key={ch.num} className="flex items-center justify-between px-4 py-3 hover:bg-background-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-foreground-400 w-16">第{ch.num}话</span>
                      <span className="text-sm text-foreground-800">{ch.title}</span>
                    </div>
                    <span className="text-xs text-foreground-400">{ch.date}</span>
                  </div>
                ))}
              </div>
            </section>

            {recommended.length > 0 && (
              <section className="mb-14">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                  {recommended.map((rec) => (
                    <MangaCard key={rec.id} manga={rec} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}